// ============================================================================
// BLOCK USS.0 — useSectionState
// ----------------------------------------------------------------------------
// State model per section:
//  - generated: baseline values from server
//  - overrides: user-edited base values
//  - locked: prevents global regenerate from overwriting overrides
// ============================================================================

import { useCallback, useState } from 'react';

export default function useSectionState(initialGenerated = {}) {
  // ==========================================================================
  // BLOCK USS.1 — State
  // ==========================================================================
  const [generated, setGenerated] = useState(initialGenerated);
  const [overrides, setOverrides] = useState({});
  const [locked, setLocked] = useState(false);

  // ==========================================================================
  // BLOCK USS.2 — Value resolution
  // ----------------------------------------------------------------------------
  // Display value = override if present, else generated
  // ==========================================================================
  const getValue = useCallback(
    function getValue(key) {
      if (Object.prototype.hasOwnProperty.call(overrides, key)) {
        return overrides[key];
      }
      return generated[key];
    },
    [generated, overrides]
  );

  // ==========================================================================
  // BLOCK USS.3 — Editable override setter
  // ==========================================================================
  const setOverrideValue = useCallback(function setOverrideValue(key, value) {
    setOverrides((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // ==========================================================================
  // BLOCK USS.4 — Global regenerate handler
  // ----------------------------------------------------------------------------
  // If locked: update generated but KEEP overrides
  // If unlocked: update generated and CLEAR overrides
  // ==========================================================================
  const applyGenerated = useCallback(
    function applyGenerated(nextGenerated) {
      setGenerated(nextGenerated);
      setOverrides((prev) => (locked ? prev : {}));
    },
    [locked]
  );

  // ==========================================================================
  // BLOCK USS.5 — Local regenerate (⭐ icon)
  // ----------------------------------------------------------------------------
  // Always resets overrides for this section
  // ==========================================================================
  const resetToGenerated = useCallback(function resetToGenerated(nextGenerated) {
    setGenerated(nextGenerated);
    setOverrides({});
  }, []);

  // ==========================================================================
  // BLOCK USS.6 — Lock toggle
  // ==========================================================================
  const toggleLock = useCallback(function toggleLock() {
    setLocked((v) => !v);
  }, []);

  return {
    generated,
    overrides,
    locked,

    getValue,
    setOverrideValue,

    applyGenerated,
    resetToGenerated,
    toggleLock,
  };
}

export function moveItem(arr, from, to) {
  const a = [...arr];
  const [v] = a.splice(from, 1);
  a.splice(to, 0, v);
  return a;
}

// ============================================================================
// BLOCK UTIL.MGS.0 — mergeGeneratedSections (string-safe, lock-driven)
// ----------------------------------------------------------------------------
// IMPORTANT:
// - Controller currently stores section values as STRINGS.
// - Global generate overwrites sections that are NOT locked.
// - Locked sections keep their existing string text.
// ============================================================================

export function mergeGeneratedSections({ prevSections, nextSections, locks }) {
  const out = { ...prevSections };

  Object.keys(nextSections || {}).forEach((k) => {
    const isLocked = Boolean(locks?.[k]);
    if (!isLocked) {
      out[k] = String(nextSections[k] || '');
    }
  });

  return out;
}

// ============================================================================
// BLOCK UTIL.LRG.0 — applyLocalRegenerate (single section)
// ----------------------------------------------------------------------------
// Local regenerate rule:
//  - always replace generated
//  - always clear overrides
//  - lock state unchanged (handled outside this function)
// ============================================================================

export function applyLocalRegenerate({ prevSections, sectionKey, nextGenerated }) {
  const out = { ...prevSections };

  const prev = prevSections?.[sectionKey];
  const prevObj =
    prev && typeof prev === 'object' && !Array.isArray(prev)
      ? prev
      : { generated: prev, overrides: {} };

  out[sectionKey] = {
    ...prevObj,
    generated: nextGenerated,
    overrides: {},
  };

  return out;
}
